import React, { useEffect, useRef, useState } from "react";
import { AddressBook, MagnifyingGlass, House, ArrowRight, CheckCircle } from "@phosphor-icons/react";

/**
 * ContactsMatchMockup — visualizzazione della rubrica con match al nuovo
 * annuncio. Sostituisce il vecchio HeroContattiMockup duplicato nella
 * sezione "Contatti & WhatsApp" (Iter 211b).
 *
 * Stile diverso dal hero (che usa il 3-step phone): qui si vede la rubrica
 * con 3 clienti e i loro criteri, il nuovo annuncio che entra, e il match
 * automatico che evidenzia 1 dei 3 con score.
 */
export default function ContactsMatchMockup() {
  const ref = useRef(null);
  const [phase, setPhase] = useState(0); // 0 idle, 1 new listing, 2 matching, 3 matched

  useEffect(() => {
    // Iter 232: parte subito al mount (componente sempre in view quando il chip è attivo).
    const t1 = setTimeout(() => setPhase(1), 600);
    const t2 = setTimeout(() => setPhase(2), 1600);
    const t3 = setTimeout(() => setPhase(3), 2800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <div
      ref={ref}
      className="relative bg-white border border-[#E5E7E0] rounded-2xl shadow-[0_18px_50px_-20px_rgba(28,28,26,0.18)] p-5 sm:p-7"
      data-testid="contacts-match-mockup"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#EAE7DF]">
        <div className="flex items-center gap-2">
          <AddressBook size={18} weight="duotone" className="text-[#7C8B6E]" />
          <div className="font-editorial text-[16px] text-[#1C1C1A]">La tua rubrica</div>
        </div>
        <div className="text-[10.5px] text-[#9A968B] uppercase tracking-[0.16em] font-bold">
          3 clienti in attesa
        </div>
      </div>

      {/* Rubrica list */}
      <div className="space-y-2 mb-5">
        {CLIENTS.map((c, i) => {
          const isMatch = c.matches && phase >= 3;
          return (
            <div
              key={i}
              className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all duration-500 ${
                isMatch
                  ? "border-[#7C8B6E] bg-[#F1F4ED] shadow-[0_0_0_3px_rgba(124,139,110,0.12)]"
                  : "border-transparent hover:bg-[#FAFAF7]"
              }`}
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
                  isMatch ? "bg-[#7C8B6E] text-white" : "bg-[#EAE7DF] text-[#3F3D38]"
                }`}
              >
                {c.initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1C1C1A] leading-tight">
                  {c.name}
                </div>
                <div className="text-[11px] text-[#6B6A65] leading-tight mt-0.5 truncate">
                  cerca: {c.criteria}
                </div>
              </div>
              {isMatch && (
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-[#7C8B6E]">
                  <CheckCircle size={12} weight="fill" /> Match
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* New listing entering */}
      <div
        className={`relative rounded-xl bg-[#FBFAF6] border border-[#E5E7E0] p-3 mb-3 transition-all duration-700 ${
          phase >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
        }`}
      >
        <div className="absolute -top-2.5 left-3 px-1.5 py-0.5 bg-[#1C1C1A] text-[#C8A66B] text-[8.5px] uppercase tracking-[0.18em] font-bold rounded">
          Nuovo annuncio
        </div>
        <div className="flex items-center gap-3 pt-1">
          <div className="w-12 h-12 rounded-lg bg-[#EAE7DF] flex items-center justify-center text-[#7C8B6E]">
            <House size={20} weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="text-[12.5px] font-semibold text-[#1C1C1A]">Trilocale · Brera</div>
            <div className="text-[10.5px] text-[#6B6A65]">95 m² · € 380.000 · balcone</div>
          </div>
        </div>
      </div>

      {/* Matching animation indicator */}
      <div className="flex items-center justify-center gap-2 text-[11px] text-[#7C8B6E] font-semibold h-6">
        {phase === 2 && (
          <>
            <MagnifyingGlass size={13} weight="bold" className="animate-pulse" />
            <span>Pronto sta cercando un match…</span>
          </>
        )}
        {phase >= 3 && (
          <div className="flex items-center gap-1.5 text-[#1C1C1A]">
            <ArrowRight size={12} weight="bold" className="text-[#7C8B6E]" />
            <span>WhatsApp pre-scritto pronto per Luca Ferrari.</span>
          </div>
        )}
      </div>
    </div>
  );
}

const CLIENTS = [
  { initials: "MR", name: "Luca Ferrari", criteria: "trilocale · Brera · ≤ 400k · balcone", matches: true },
  { initials: "AL", name: "Anna Lorenzi", criteria: "monolocale · Navigli · ≤ 250k" },
  { initials: "PB", name: "Paolo Bianchi", criteria: "villa · Como · giardino + piscina" },
];
